import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";

interface RequireAdminProps {
  children: React.ReactNode;
}

/**
 * Gate for /admin/*.
 * - No session  → /auth (with `from` so post-login returns here)
 * - Session but not admin → / (silent)
 * - Admin → render children
 *
 * Frontend gate only. Server-side enforcement (every admin edge function
 * must call has_role itself) is the real security boundary.
 */
export const RequireAdmin = ({ children }: RequireAdminProps) => {
  const { session, isAuthReady } = useAuth();
  const { isAdmin, isReady } = useIsAdmin();
  const location = useLocation();

  if (!isAuthReady || !isReady) {
    // Render a neutral placeholder so admin shell doesn't flash unstyled
    // and non-admins don't briefly see denial UI.
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0b0d10",
        }}
      />
    );
  }

  if (!session) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
